<template>
  <div>
    <v-container fluid>
      <v-card elevation="6" rounded="xxl">
        <v-card-title class="mt-4">
          <v-btn
            class="mx-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark> mdi-arrow-left </v-icon> </v-btn
          >Edit Investment
        </v-card-title>
        <v-card-text>
          <section v-if="isLoading" style="height: 100px">
            <v-progress-linear
              indeterminate
              color="primary"
            ></v-progress-linear>
          </section>
          <v-row v-else>
            <v-col sm="6">
              <v-autocomplete
                v-model="form.user_id"
                :items="users"
                outlined
                label="Select User"
                prepend-inner-icon="mdi-account"
                :error-messages="errors.user_id"
                item-text="text"
                item-value="value"
                :loading="loadingMasters"
                clearable
                :disabled="loadingMasters"
              ></v-autocomplete>
            </v-col>
            <v-col sm="6">
              <v-autocomplete
                v-model="form.product_id"
                :items="products"
                outlined
                label="Select Product"
                prepend-inner-icon="mdi-cube-outline"
                :error-messages="errors.product_id"
                item-text="text"
                item-value="value"
                :loading="loadingMasters"
                clearable
                :disabled="loadingMasters"
              ></v-autocomplete>
            </v-col>
          </v-row>
          <v-row>
            <v-col sm="6">
              <v-text-field
                v-model="form.invested_amount"
                outlined
                label="Investment Amount"
                prepend-inner-icon="mdi-currency-usd"
                :error-messages="errors.invested_amount"
                type="number"
                min="0"
                step="0.01"
              ></v-text-field>
            </v-col>
            <v-col sm="6">
              <v-autocomplete
                v-model="form.payment_mode"
                :items="paymentModes"
                outlined
                label="Payment Mode"
                prepend-inner-icon="mdi-credit-card"
                :error-messages="errors.payment_mode"
                item-text="text"
                item-value="value"
                clearable
              ></v-autocomplete>
            </v-col>
          </v-row>
          <v-row>
            <v-col sm="6">
              <v-autocomplete
                v-model="form.status"
                :items="statusOptions"
                outlined
                label="Investment Status"
                prepend-inner-icon="mdi-clipboard-check"
                :error-messages="errors.status"
                item-text="text"
                item-value="value"
                clearable
              ></v-autocomplete>
            </v-col>
            <v-col sm="6" style="display:flex; align-items:center; padding-bottom:26px;">
              <v-switch
                v-model="form.is_active"
                :true-value="1"
                :false-value="0"
                label="Active"
                color="primary"
                inset
                hide-details
                class="mt-0 pt-0"
              ></v-switch>
            </v-col>
          </v-row>
          <v-divider class="my-5"> </v-divider>
          <v-btn
            rounded
            color="primary"
            :loading="isLoading"
            :disabled="isLoading"
            @click="save"
          >
            Update Investment
          </v-btn>
        </v-card-text>
      </v-card>

      <!-- Snackbar for error/success messages -->
      <v-snackbar
        v-model="snackbar.show"
        :color="snackbar.color"
        :timeout="snackbar.timeout"
        top
        right
      >
        {{ snackbar.message }}
        <template v-slot:action="{ attrs }">
          <v-btn
            color="white"
            text
            v-bind="attrs"
            @click="snackbar.show = false"
          >
            Close
          </v-btn>
        </template>
      </v-snackbar>
    </v-container>
  </div>
</template>
<script>
import axios from "axios";

export default {
  name: "InvestmentEditPage",
  data() {
    return {
      form: {
        user_id: "",
        product_id: "",
        invested_amount: "",
        payment_mode: "",
        status: 0,
        is_active: 1,
      },
      users: [],
      products: [],
      paymentModes: [
        { text: "Wallet", value: 1 },
        { text: "Online", value: 2 },
        { text: "Cash", value: 3 },
      ],
      statusOptions: [
        { text: "Pending", value: 0 },
        { text: "Approved", value: 1 },
        { text: "Rejected", value: 2 },
      ],
      isLoading: false,
      loadingMasters: false,
      errors: {},
      old_json: {},
      snackbar: {
        show: false,
        message: '',
        color: 'success',
        timeout: 4000,
      },
    };
  },
  mounted() {
    this.getMasters();
    this.getData();
  },
  methods: {
    async getMasters() {
      try {
        this.loadingMasters = true;
        const response = await axios.get('masters?context=investments');
        
        if (response.data?.users) {
          this.users = response.data.users.map(user => ({
            text: `${user.first_name || ''} ${user.last_name || ''}`.trim() || user.email || `User ${user.id}`,
            value: user.id,
          }));
        }
        
        if (response.data?.products) {
          this.products = response.data.products.map(product => ({
            text: product.product_name || `Product ${product.id}`,
            value: product.id,
          }));
        }
      } catch (error) {
        console.error('Error loading dropdown data:', error);
        this.showSnackbar('Failed to load dropdown data. Please refresh the page.', 'error');
      } finally {
        this.loadingMasters = false;
      }
    },
    showSnackbar(message, color = 'success') {
      this.snackbar.message = message;
      this.snackbar.color = color;  
      this.snackbar.show = true;
    },
    async getData() {
      try {
        this.isLoading = true;
        let encryptedId = this.$route.params.investmentId;
        const response = await axios.get(`/investments/${encryptedId}`);
        this.form = { ...response.data.data };
        this.old_json = { ...response.data.data };
      } catch (error) {
        console.error("Error fetching data:", error);
        this.showSnackbar('Failed to load investment data. Please refresh the page.', 'error');
      } finally {
        this.isLoading = false;
      }
    },
    async save() {
      try {
        this.isLoading = true;
        this.errors = {}; // Clear previous errors
        
        await axios.patch(`/investments/${this.$route.params.investmentId}`, this.form);
        await this.saveUserTimestamp(this.old_json, this.form);
        this.showSnackbar('Investment updated successfully!', 'success');
        this.isLoading = false;
      } catch (e) {
        console.log(e);
        this.isLoading = false;
        
        // Handle validation errors
        if (e.response && e.response.status === 422) {
          this.errors = e.response.data.errors || {};
          this.showSnackbar('Please check the form for validation errors.', 'error');
        } else if (e.response && e.response.data) {
          // Show API error message
          const message = e.response.data.message || 'Failed to update investment';
          this.showSnackbar(message, 'error');
        } else {
          this.showSnackbar('Failed to update investment. Please try again.', 'error');
        }
      }
    },
    async saveUserTimestamp() {
      try {
        let form = {
          from_path: `/investment/${this.form.id}`,
          from_name: "InvestmentEditPage",
          user_id: this.user.id,
          old_json: JSON.stringify(this.old_json),
          new_json: JSON.stringify(this.form),
        };
        await axios.post(`/user_timestamps`, form);
        this.$router.back();
      } catch (error) {
        console.log(error);
      }
    },
  },
};
</script>
